import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PortalPage, Spinner, ErrorNote } from "@/components/PortalPage";
import { api } from "@/lib/api";
import { BkTabs, EntitySwitcher, useBkEntities } from "@/components/bookkeeping/BkShell";

const Select = ({ value, entities, onChange, testId }) => (
  <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
    className="rounded-lg border border-[#333] bg-[#0F0F0F] px-3 py-2 text-sm text-white outline-none focus:border-[#4CCC06]">
    {entities.map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
  </select>
);

export default function EntityMapping() {
  const { entities, error, entityId, setEntityId } = useBkEntities();
  const [data, setData] = useState(null);
  const load = () => api("/bookkeeping/mappings").then(setData).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  const change = async (key, eid) => {
    try { await api(`/bookkeeping/mappings/${key}`, { method: "PUT", body: { entity_id: eid } }); toast.success("Mapping saved"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const removeOverride = async (o) => {
    try { await api(`/bookkeeping/mappings/${o.source_type}/records/${o.source_id}`, { method: "DELETE" }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const name = (id) => entities?.find((e) => e.id === id)?.name || id;
  return (
    <PortalPage eyebrow="BOOKKEEPING" title="Entity mapping" back="/bookkeeping" testId="bookkeeping-mapping" width="max-w-5xl">
      {error && <ErrorNote>{error}</ErrorNote>}
      {!entities || !data ? <Spinner /> : (
        <>
          <EntitySwitcher entities={entities} entityId={entityId} onChange={setEntityId} />
          <BkTabs />
          <p className="text-sm text-neutral-400">Which entity's books each kind of portal record lands in. Individual records can be overridden later from the transaction screens.</p>
          <div className="divide-y divide-[#262626] rounded-xl border border-[#333] bg-[#141414]" data-testid="bk-mapping-list">
            {data.source_types.map((s) => (
              <div key={s.key} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`bk-mapping-${s.key}`}
                style={s.entity_id === entityId ? { background: "#4CCC060D" } : undefined}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-white">{s.label}</p>
                  <p className="text-xs text-neutral-500">{s.collection ? `From ${s.collection}` : "Not tracked in the portal yet"}</p>
                </div>
                <Select testId={`bk-mapping-select-${s.key}`} value={s.entity_id} entities={entities} onChange={(eid) => change(s.key, eid)} />
              </div>
            ))}
          </div>
          <section data-testid="bk-overrides">
            <p className="mb-2 text-[11px] font-bold tracking-[3px] text-[#4CCC06]">PER-RECORD OVERRIDES</p>
            {data.overrides.length === 0 ? <p className="text-sm text-neutral-500">None yet.</p> : data.overrides.map((o) => (
              <div key={o.id} className="flex items-center justify-between rounded-lg border border-[#262626] px-3 py-2 text-sm" data-testid={`bk-override-${o.source_id}`}>
                <span>{o.source_type} · {o.source_id} → <b>{name(o.entity_id)}</b></span>
                <button onClick={() => removeOverride(o)} className="text-xs font-bold text-[#FF6B6B]" data-testid={`bk-override-remove-${o.source_id}`}>Remove</button>
              </div>
            ))}
          </section>
        </>
      )}
    </PortalPage>
  );
}
